'use client';

import { c as _c } from "react/compiler-runtime";
import { useAuth, usePrefetchGetLeaderboard } from '@morfi/backend-sdk-v2/client';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { useEffect, useOptimistic, useRef, useState, useTransition } from 'react';
import { SocialBoardPanels } from '@/components/social/social-board-panels';
import { SocialPodiumBlock } from '@/components/social/social-podium-block';
import type { BackerSort, BoosterSort, CreatorSort, PnlSort, SocialTab, TabJump } from '@/components/social/types';
import { SocialYourRankBar } from '@/components/social/your-rank-bar';
import { Tabs } from '@/components/ui/app-tabs';
import { SegmentedControl, type SegmentedControlItem } from '@/components/ui/segmented-control';
import { onEvent } from '@/events';
import { useAppRouter } from '@/lib/network/use-app-router';
import { LB_FIELDS_BY_TAB } from '@/lib/sdk/projections';
const VALID_TABS: readonly SocialTab[] = ['leaderboard', 'backers', 'creators', 'boosters'];
const DEFAULT_TAB: SocialTab = 'leaderboard';

// The unified leaderboard `kind` uses `traders` where the tab is `leaderboard`.
const TAB_TO_KIND: Record<SocialTab, 'traders' | 'boosters' | 'creators' | 'backers'> = {
  leaderboard: 'traders',
  boosters: 'boosters',
  creators: 'creators',
  backers: 'backers'
};

// Per-tab anchor-jump + pulse pair driven by the floating rank bar (spec 074).
// The `TabJump` shape lives in `./types` so `SocialBoardPanels` shares it.
const EMPTY_TAB_JUMP: TabJump = {
  anchor: undefined,
  pulse: undefined
};

// Time-window selector (?window=). Mirrors the tab/sort URL-sync pattern:
// `lifetime` is the default and is stripped from the URL to keep it clean.
// The values are passed straight through to the v2 windowed leaderboard hooks.
const VALID_WINDOWS = ['1d', '1w', '1m', '1y', 'lifetime'] as const;
type SocialWindow = (typeof VALID_WINDOWS)[number];
const DEFAULT_WINDOW: SocialWindow = 'lifetime';

// Per-tab valid sort keys; keep in sync with each tab's `Sort` union type.
const VALID_PNL_SORTS: readonly PnlSort[] = ['pnl', 'volume', 'trades', 'markets'];
const VALID_BOOSTER_SORTS: readonly BoosterSort[] = ['amount', 'boosts', 'marketsBoosted'];
const VALID_CREATOR_SORTS: readonly CreatorSort[] = ['topMarket', 'marketsCreated', 'totalVolume'];
const VALID_BACKER_SORTS: readonly BackerSort[] = ['amount', 'backs', 'top_market'];
const DEFAULT_PNL_SORT: PnlSort = 'pnl';
const DEFAULT_BOOSTER_SORT: BoosterSort = 'amount';
const DEFAULT_CREATOR_SORT: CreatorSort = 'totalVolume';
const DEFAULT_BACKER_SORT: BackerSort = 'amount';

// Default wire `sortBy` per tab (used when no `?sort=` is set). Drives the
// podium prefetch so a tab switch lands its top-3 already in cache.
const DEFAULT_SORT_BY_TAB: Record<SocialTab, string> = {
  leaderboard: DEFAULT_PNL_SORT,
  boosters: DEFAULT_BOOSTER_SORT,
  creators: DEFAULT_CREATOR_SORT,
  backers: DEFAULT_BACKER_SORT
};
function isValidTab(value: string | null): value is SocialTab {
  return value !== null && (VALID_TABS as readonly string[]).includes(value);
}
function parsePnlSort(raw: string | null): PnlSort {
  return raw !== null && (VALID_PNL_SORTS as readonly string[]).includes(raw) ? raw as PnlSort : DEFAULT_PNL_SORT;
}
function parseBoosterSort(raw: string | null): BoosterSort {
  return raw !== null && (VALID_BOOSTER_SORTS as readonly string[]).includes(raw) ? raw as BoosterSort : DEFAULT_BOOSTER_SORT;
}
function parseCreatorSort(raw: string | null): CreatorSort {
  return raw !== null && (VALID_CREATOR_SORTS as readonly string[]).includes(raw) ? raw as CreatorSort : DEFAULT_CREATOR_SORT;
}
function parseBackerSort(raw: string | null): BackerSort {
  return raw !== null && (VALID_BACKER_SORTS as readonly string[]).includes(raw) ? raw as BackerSort : DEFAULT_BACKER_SORT;
}
function parseWindow(raw: string | null): SocialWindow {
  return raw !== null && (VALID_WINDOWS as readonly string[]).includes(raw) ? raw as SocialWindow : DEFAULT_WINDOW;
}
interface SocialClientProps {
  /** The route-owned competition banner; its data owner decides whether it renders. */
  competitionBanner?: ReactNode;
}
export function SocialClient(t0) {
  const $ = _c(97);
  const {
    competitionBanner: t1
  } = t0;
  const competitionBanner = t1 === undefined ? null : t1;
  const t = useTranslations("social.tabs");
  const tWindow = useTranslations("social.window");
  const router = useAppRouter();
  const searchParams = useSearchParams();
  const {
    isAuthenticated
  } = useAuth();
  const prefetchLeaderboard = usePrefetchGetLeaderboard();
  let rawTab;
  let t2;
  if ($[0] !== searchParams) {
    rawTab = searchParams.get("tab");
    t2 = isValidTab(rawTab) ? rawTab : DEFAULT_TAB;
    $[0] = searchParams;
    $[1] = rawTab;
    $[2] = t2;
  } else {
    rawTab = $[1];
    t2 = $[2];
  }
  const activeTab = t2;
  const rawSort = searchParams.get("sort");
  let t3;
  if ($[3] !== searchParams) {
    t3 = parseWindow(searchParams.get("window"));
    $[3] = searchParams;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const activeWindow = t3;
  const [, startTabTransition] = useTransition();
  const [optimisticTab, setOptimisticTab] = useOptimistic(activeTab, _temp);
  const [, startWindowTransition] = useTransition();
  const [optimisticWindow, setOptimisticWindow] = useOptimistic(activeWindow, _temp2);
  const prevTabRef = useRef(activeTab);
  let t4;
  let t5;
  if ($[5] !== rawTab || $[6] !== router || $[7] !== searchParams) {
    t4 = () => {
      if (rawTab === null || isValidTab(rawTab)) {
        return;
      }
      const params = new URLSearchParams(searchParams.toString());
      params.delete("tab");
      const queryString = params.toString();
      const newPath = queryString.length > 0 ? `?${queryString}` : "";
      router.replace(`/social${newPath}`, {
        scroll: false
      });
    };
    t5 = [rawTab, router, searchParams];
    $[5] = rawTab;
    $[6] = router;
    $[7] = searchParams;
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  useEffect(t4, t5);
  const hasLoggedPageLoad = useRef(false);
  let t6;
  let t7;
  if ($[10] !== activeTab || $[11] !== isAuthenticated) {
    t6 = () => {
      if (hasLoggedPageLoad.current) {
        return;
      }
      hasLoggedPageLoad.current = true;
      onEvent("social_page_loaded", {
        tab: activeTab,
        authenticated: isAuthenticated
      });
    };
    t7 = [activeTab, isAuthenticated];
    $[10] = activeTab;
    $[11] = isAuthenticated;
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  useEffect(t6, t7);
  let t8;
  if ($[14] !== activeTab || $[15] !== optimisticWindow || $[16] !== prefetchLeaderboard) {
    t8 = function handleTabPointerEnter(key) {
      if (!isValidTab(key) || key === activeTab) {
        return;
      }
      prefetchLeaderboard({
        kind: TAB_TO_KIND[key],
        sortBy: DEFAULT_SORT_BY_TAB[key],
        sortOrder: "desc",
        window: optimisticWindow,
        fields: [...LB_FIELDS_BY_TAB[key]]
      });
    };
    $[14] = activeTab;
    $[15] = optimisticWindow;
    $[16] = prefetchLeaderboard;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const handleTabPointerEnter = t8;
  let t9;
  if ($[18] !== router || $[19] !== searchParams) {
    t9 = function writeUrl(tab, sort) {
      const params_0 = new URLSearchParams(searchParams.toString());
      if (tab === DEFAULT_TAB) {
        params_0.delete("tab");
      } else {
        params_0.set("tab", tab);
      }
      if (sort === undefined) {
        params_0.delete("sort");
      } else {
        params_0.set("sort", sort);
      }
      const queryString_0 = params_0.toString();
      const newPath_0 = queryString_0.length > 0 ? `?${queryString_0}` : "";
      router.replace(`/social${newPath_0}`, {
        scroll: false
      });
    };
    $[18] = router;
    $[19] = searchParams;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const writeUrl = t9;
  let t10;
  if ($[21] !== router || $[22] !== searchParams || $[23] !== setOptimisticWindow || $[24] !== startWindowTransition) {
    t10 = function handleWindowChange(value) {
      if (!(VALID_WINDOWS as readonly string[]).includes(value)) {
        return;
      }
      const nextWindow = value as SocialWindow;
      startWindowTransition(() => {
        setOptimisticWindow(nextWindow);
        const params_1 = new URLSearchParams(searchParams.toString());
        if (nextWindow === DEFAULT_WINDOW) {
          params_1.delete("window");
        } else {
          params_1.set("window", nextWindow);
        }
        const queryString_1 = params_1.toString();
        const newPath_1 = queryString_1.length > 0 ? `?${queryString_1}` : "";
        router.replace(`/social${newPath_1}`, {
          scroll: false
        });
      });
    };
    $[21] = router;
    $[22] = searchParams;
    $[23] = setOptimisticWindow;
    $[24] = startWindowTransition;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const handleWindowChange = t10;
  let t11;
  if ($[26] !== setOptimisticTab || $[27] !== startTabTransition || $[28] !== writeUrl) {
    t11 = function handleTabChange(value_0) {
      if (!isValidTab(value_0)) {
        return;
      }
      const previousTab = prevTabRef.current;
      prevTabRef.current = value_0;
      onEvent("social_tab_switched", {
        tab: value_0,
        previousTab
      });
      startTabTransition(() => {
        setOptimisticTab(value_0);
        writeUrl(value_0, undefined);
      });
    };
    $[26] = setOptimisticTab;
    $[27] = startTabTransition;
    $[28] = writeUrl;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  const handleTabChange = t11;
  let t12;
  if ($[30] !== activeTab || $[31] !== writeUrl) {
    t12 = function handlePnlSortChange(next_1) {
      writeUrl(activeTab, next_1 === DEFAULT_PNL_SORT ? undefined : next_1);
    };
    $[30] = activeTab;
    $[31] = writeUrl;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  const handlePnlSortChange = t12;
  let t13;
  if ($[33] !== activeTab || $[34] !== writeUrl) {
    t13 = function handleBoosterSortChange(next_2) {
      writeUrl(activeTab, next_2 === DEFAULT_BOOSTER_SORT ? undefined : next_2);
    };
    $[33] = activeTab;
    $[34] = writeUrl;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  const handleBoosterSortChange = t13;
  let t14;
  if ($[36] !== activeTab || $[37] !== writeUrl) {
    t14 = function handleCreatorSortChange(next_3) {
      writeUrl(activeTab, next_3 === DEFAULT_CREATOR_SORT ? undefined : next_3);
    };
    $[36] = activeTab;
    $[37] = writeUrl;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  const handleCreatorSortChange = t14;
  let t15;
  if ($[39] !== activeTab || $[40] !== writeUrl) {
    t15 = function handleBackerSortChange(next_4) {
      writeUrl(activeTab, next_4 === DEFAULT_BACKER_SORT ? undefined : next_4);
    };
    $[39] = activeTab;
    $[40] = writeUrl;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  const handleBackerSortChange = t15;
  const [tabJumps, setTabJumps] = useState(_temp3);
  let t16;
  if ($[42] !== optimisticTab) {
    t16 = userId => {
      setTabJumps(prev => ({
        ...prev,
        [optimisticTab]: {
          anchor: userId,
          pulse: (prev[optimisticTab].pulse ?? 0) + 1
        }
      }));
    };
    $[42] = optimisticTab;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  const handleAnchorJump = t16;
  const activePnlSort = parsePnlSort(rawSort);
  const activeBoosterSort = parseBoosterSort(rawSort);
  const activeCreatorSort = parseCreatorSort(rawSort);
  const activeBackerSort = parseBackerSort(rawSort);
  const activeSortForTab = optimisticTab === "leaderboard" ? activePnlSort : optimisticTab === "boosters" ? activeBoosterSort : optimisticTab === "creators" ? activeCreatorSort : activeBackerSort;
  let t17;
  if ($[44] !== t) {
    t17 = VALID_TABS.map(tab_0 => ({
      key: tab_0,
      label: t(tab_0)
    }));
    $[44] = t;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  const tabItems = t17;
  let t18;
  if ($[46] !== tWindow) {
    t18 = VALID_WINDOWS.map(w => ({
      key: w,
      label: tWindow(w)
    }));
    $[46] = tWindow;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  const windowItems = t18;
  let t19;
  if ($[48] !== activeSortForTab || $[49] !== optimisticTab || $[50] !== optimisticWindow) {
    t19 = <SocialPodiumBlock tab={optimisticTab} timeWindow={optimisticWindow} sortBy={activeSortForTab} />;
    $[48] = activeSortForTab;
    $[49] = optimisticTab;
    $[50] = optimisticWindow;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== handleTabChange || $[53] !== handleTabPointerEnter || $[54] !== optimisticTab || $[55] !== tabItems) {
    t20 = <SegmentedControl variant="tab-surface" asTabs={true} items={tabItems} active={optimisticTab} onChange={handleTabChange} onItemPointerEnter={handleTabPointerEnter} layoutId="social-primary-tabs-pill" className="w-fit max-w-full overflow-x-auto scrollbar-none rounded-pill bg-background-tertiary p-1" />;
    $[52] = handleTabChange;
    $[53] = handleTabPointerEnter;
    $[54] = optimisticTab;
    $[55] = tabItems;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  let t21;
  if ($[57] !== tWindow) {
    t21 = tWindow("aria_label");
    $[57] = tWindow;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[59] !== handleWindowChange || $[60] !== optimisticWindow || $[61] !== t21 || $[62] !== windowItems) {
    t22 = <SegmentedControl variant="tab-surface" asTabs={true} items={windowItems} active={optimisticWindow} onChange={handleWindowChange} layoutId="social-window-pill" ariaLabel={t21} className="w-fit max-w-full overflow-x-auto scrollbar-none rounded-pill bg-background-tertiary p-1" />;
    $[59] = handleWindowChange;
    $[60] = optimisticWindow;
    $[61] = t21;
    $[62] = windowItems;
    $[63] = t22;
  } else {
    t22 = $[63];
  }
  let t23;
  if ($[64] !== t20 || $[65] !== t22) {
    t23 = <div className="flex w-full flex-col items-center gap-3">{t20}{t22}</div>;
    $[64] = t20;
    $[65] = t22;
    $[66] = t23;
  } else {
    t23 = $[66];
  }
  let t24;
  if ($[67] !== activeBackerSort || $[68] !== activeBoosterSort || $[69] !== activeCreatorSort || $[70] !== activePnlSort || $[71] !== handleBackerSortChange || $[72] !== handleBoosterSortChange || $[73] !== handleCreatorSortChange || $[74] !== handlePnlSortChange || $[75] !== optimisticWindow || $[76] !== tabJumps) {
    t24 = <SocialBoardPanels timeWindow={optimisticWindow} tabJumps={tabJumps} pnlSort={activePnlSort} boosterSort={activeBoosterSort} creatorSort={activeCreatorSort} backerSort={activeBackerSort} onPnlSortChange={handlePnlSortChange} onBoosterSortChange={handleBoosterSortChange} onCreatorSortChange={handleCreatorSortChange} onBackerSortChange={handleBackerSortChange} />;
    $[67] = activeBackerSort;
    $[68] = activeBoosterSort;
    $[69] = activeCreatorSort;
    $[70] = activePnlSort;
    $[71] = handleBackerSortChange;
    $[72] = handleBoosterSortChange;
    $[73] = handleCreatorSortChange;
    $[74] = handlePnlSortChange;
    $[75] = optimisticWindow;
    $[76] = tabJumps;
    $[77] = t24;
  } else {
    t24 = $[77];
  }
  let t25;
  if ($[78] !== handleTabChange || $[79] !== optimisticTab || $[80] !== t19 || $[81] !== t23 || $[82] !== t24) {
    t25 = <Tabs value={optimisticTab} onValueChange={handleTabChange} className="gap-6">{t19}{t23}{t24}</Tabs>;
    $[78] = handleTabChange;
    $[79] = optimisticTab;
    $[80] = t19;
    $[81] = t23;
    $[82] = t24;
    $[83] = t25;
  } else {
    t25 = $[83];
  }
  let t26;
  if ($[84] !== activeBackerSort || $[85] !== activeBoosterSort || $[86] !== activeCreatorSort || $[87] !== activePnlSort || $[88] !== handleAnchorJump || $[89] !== isAuthenticated || $[90] !== optimisticTab || $[91] !== optimisticWindow) {
    t26 = isAuthenticated && <SocialYourRankBar activeTab={optimisticTab} activeWindow={optimisticWindow} activeSort={optimisticTab === "leaderboard" ? activePnlSort : optimisticTab === "boosters" ? activeBoosterSort : optimisticTab === "creators" ? activeCreatorSort : activeBackerSort} onAnchorJump={handleAnchorJump} />;
    $[84] = activeBackerSort;
    $[85] = activeBoosterSort;
    $[86] = activeCreatorSort;
    $[87] = activePnlSort;
    $[88] = handleAnchorJump;
    $[89] = isAuthenticated;
    $[90] = optimisticTab;
    $[91] = optimisticWindow;
    $[92] = t26;
  } else {
    t26 = $[92];
  }
  let t27;
  if ($[93] !== competitionBanner || $[94] !== t25 || $[95] !== t26) {
    t27 = <div data-testid="social-page" className="flex flex-col gap-6">{competitionBanner}{t25}{t26}</div>;
    $[93] = competitionBanner;
    $[94] = t25;
    $[95] = t26;
    $[96] = t27;
  } else {
    t27 = $[96];
  }
  return t27;
}
function _temp3() {
  return {
    leaderboard: EMPTY_TAB_JUMP,
    boosters: EMPTY_TAB_JUMP,
    creators: EMPTY_TAB_JUMP,
    backers: EMPTY_TAB_JUMP
  };
}
function _temp2(_prev_0, next_0) {
  return next_0;
}
function _temp(_prev, next) {
  return next;
}